import React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import HeadsetMicIcon from "@mui/icons-material/HeadsetMic";
import PersonIcon from "@mui/icons-material/Person";

/**
 * Asks a Host how they want to enter a session.
 *
 * Only Hosts see this. Being an Admin is reach, not the right to run a
 * session — an Admin joins as an attendee with no prompt, the same split
 * apps-video enforces between `isStaff` and `hasHostPrivileges`.
 *
 * A dialog rather than a menu anchored to the row's icon: the list is used on
 * mobile, where the action collapses into an overflow menu and a popover
 * hanging off a small icon inside another popover is awkward to hit.
 */
interface JoinMeetingDialogProps {
  open: boolean;
  meetingTitle: string;
  /** True while the join is being resolved — holds both choices. */
  isResolving: boolean;
  onClose: () => void;
  onJoin: (options: { asHost: boolean }) => void;
}

export const JoinMeetingDialog: React.FC<JoinMeetingDialogProps> = ({
  open,
  meetingTitle,
  isResolving,
  onClose,
  onJoin,
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
    <DialogTitle>Join Session</DialogTitle>
    <DialogContent>
      <Typography variant="body2" color="text.secondary">
        You are joining
      </Typography>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
        {meetingTitle}
      </Typography>

      <Stack spacing={1.5}>
        <Button
          variant="contained"
          size="large"
          startIcon={<HeadsetMicIcon />}
          disabled={isResolving}
          onClick={() => onJoin({ asHost: true })}
        >
          Join as Host
        </Button>
        {/* Said out loud because the action is not additive: joining as host
            runs a takeover, and whoever is hosting right now loses host
            capabilities the moment this lands. */}
        <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
          Takes over hosting from whoever is running the session.
        </Typography>
        <Button
          variant="outlined"
          size="large"
          startIcon={<PersonIcon />}
          disabled={isResolving}
          onClick={() => onJoin({ asHost: false })}
        >
          Join as Attendee
        </Button>
      </Stack>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} disabled={isResolving}>
        Cancel
      </Button>
    </DialogActions>
  </Dialog>
);
